import { useEffect, useId } from 'react';
import { useDispatch } from 'react-redux';

import { BackInfoActions } from '../../Reducer/BackInfoReducer';
import {
  registerBackHandler,
  unregisterBackHandler,
} from './backHandlerRegistry';

/**
 * 컴포넌트가 마운트될 때 BackStack에 guard를 등록하고
 * 언마운트 or enabled=false 시 자동으로 제거
 *
 * @param {object}   options
 * @param {string}   options.type     - 'sheet' | 'dialog' | 'drawer' | 'snackbar' | 'navigate' | 'block' | 'custom'
 * @param {function} options.onBack   - 뒤로가기 시 실행할 함수
 * @param {boolean}  [options.enabled=true] - 조건부 활성화
 *
 * @example
 * // 항상 등록
 * useBackStack({
 *   type: 'sheet',
 *   onBack: () => openSheet('backClickTbmEnd'),
 * });
 *
 * @example
 * // 조건부 등록 (결제 중일 때만 차단)
 * useBackStack({
 *   type: 'snackbar',
 *   enabled: isProcessing,
 *   onBack: () => dispatch(showSnackbar({ message: '결제 중에는 뒤로갈 수 없어요.' })),
 * });
 */
export function useBackStack({ type, onBack, enabled = true }) {
  const dispatch = useDispatch();
  const id = useId();

  useEffect(() => {
    if (!enabled) return;

    const handlerKey = `back_${id}`;

    // 1. 핸들러 registry에 등록
    registerBackHandler(handlerKey, onBack);

    // 2. Redux 스택에 push
    dispatch(BackInfoActions.push({ id, type, handlerKey }));

    return () => {
      // 언마운트 or enabled 변경 시 자동 정리
      unregisterBackHandler(handlerKey);
      dispatch(BackInfoActions.removeById(id));
    };
    // onBack은 매 렌더마다 새 참조가 생길 수 있으므로 의존성에서 제외
    // 필요 시 useCallback으로 감싸서 넘길 것
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
